import type { HttpErrorResponse } from '@angular/common/http';
import type { Params } from '@angular/router';
import type { EduCourse } from '@common/types/edu-course.type';

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { CollectionError } from '@common/adapters/collections';
import { Widgets } from '@common/enums';
import { isUuid } from '@common/helpers';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { EduCoursesHttpService } from './edu-courses.http.service';

import { AppFacade } from '@web/store/app/app.facade';
import { RouterFacade } from '@web/store/router/router.facade';

import AppActions from '@web/store/app/app.actions';
import NotifyAction from '@common/utils/notify/notify.actions';
import EduCoursesActions from './edu-courses.actions';

@Injectable()
export class EduCoursesCollectionEffect {
    private readonly actions = inject(Actions);
    private readonly translateService = inject(TranslateService);
    private readonly widgetsService = inject(WebWidgetsService);
    private readonly eduCoursesHttpService = inject(EduCoursesHttpService);
    private readonly appFacade = inject(AppFacade);
    private readonly routerFacade = inject(RouterFacade);

    public readonly loadEduCoursesCollection$ = createEffect(() =>
        this.actions.pipe(
            ofType(AppActions.initAppSuccess),
            withLatestFrom(this.appFacade.isAdmin$, (_, isAdmin: boolean) => ({
                isAdmin,
            })),
            filter(({ isAdmin }) => !isAdmin),
            switchMap(() => this.eduCoursesHttpService.getEduCourses()),
            map((courses: EduCourse[]) =>
                EduCoursesActions.loadCollectionSuccess({
                    courses,
                })
            ),
            catchError((errorResponse: HttpErrorResponse) => {
                if (errorResponse.status === 401) {
                    return [EduCoursesActions.loadCollectionFailure({ error: CollectionError.unauthorized })];
                }

                return [
                    EduCoursesActions.loadCollectionFailure({ error: CollectionError.unknown }),
                    NotifyAction.showNotify({
                        toastType: 'error',
                        message: this.translateService.instant('unknown_error'),
                    }),
                ];
            }) // TODO: Add error handling
        )
    );

    public readonly setLoginEduWidgetAsVisible$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(EduCoursesActions.loadCollectionSuccess),
                withLatestFrom(this.routerFacade.routerParams$, ({ courses }, routerParams: Params) => ({
                    routerParams,
                    courses,
                })),
                filter(
                    ({ courses, routerParams }) =>
                        !!routerParams.siteId &&
                        !isUuid(routerParams.siteId) &&
                        courses.filter((course: EduCourse) => course?.landingId === Number(routerParams.siteId))
                            .length > 0
                ),
                tap(() => {
                    this.widgetsService.setWidgetAsAvailable(Widgets.LOGIN_EDU);
                    this.widgetsService.setWidgetAsAvailable(Widgets.EDU_LOGIN_BUTTON);
                })
            ),
        { dispatch: false }
    );
}
